import React, { useEffect, useState } from 'react';
import Common from '../../../Components/Layouts/Common';
import { useNavigate } from "react-router-dom";
import { fetchCityList } from '../../../Utils/cityProfileUtils';
import useApi from '../../../Hooks/useApi';
import DropDown from '../../../Components/UI/Inputs/DropDown';
import {
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useDropzone } from 'react-dropzone';
import showToast from '../../../Helpers/toast';
import { handleApiError } from "../../../Helpers/ErrorHandler.js";

function InsertMapData() {
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMapDataType, setSelectedMapDataType] = useState('');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [fileSuccess, setFileSuccess] = useState("");
  const [cityList, setCityList] = useState([]);
  const { request, loading } = useApi();


  const navigate = useNavigate();



  useEffect(() => {
    const fetchCityData = async () => {
      await fetchCityList(request, setCityList);
    };
    fetchCityData();
  }, [request]);


  // File drop handling
  const onDrop = (acceptedFiles) => {
    handleFile(acceptedFiles[0]);
  };
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept:
      selectedMapDataType?.value === "2"
        ? {
          // Accept Excel for Infrastructure Data
          "application/vnd.ms-excel": [".xls"],
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
        }
        : {
          // Accept .shp for Ward Data
          "application/octet-stream": [".shp"],
        },
    multiple: false,
  });


  const handleFile = (selectedFile) => {
    const isExcel =
      selectedFile.type === "application/vnd.ms-excel" ||
      selectedFile.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    const isShapefile = selectedFile.name.endsWith(".shp");

    if (
      (selectedMapDataType?.value === "2" && isExcel) || // Excel for Infrastructure
      (selectedMapDataType?.value === "1" && isShapefile) // SHP for Ward
    ) {
      setFile(selectedFile);
      setFileError("");
      setFileSuccess(`File "${selectedFile.name}" selected`);
    } else {
      setFile(null);
      setFileSuccess("");
      setFileError(
        selectedMapDataType?.value === "1"
          ? "Please upload a .shp file for Ward Data"
          : "Please upload a valid Excel file (.xls or .xlsx) for Infrastructure Data"
      );
    }
  };



  // Handle file upload
  const handleFileUpload = async () => {
    if (!file) {
      setFileError("No file selected. Please choose a file.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file, file.name);

    try {
      const response = await request(
        // "POST",
        // `/v1/survey/questionnaires/upload?body=${bodyParam}`,
        // {
        //   data: formData,
        //   headers: {
        //     "Content-Type": "multipart/form-data",
        //   },
        // },
        // "survey_service"
      );

      if (response) {
        showToast({ message: "File Uploaded successfully", type: "success" });
        setTimeout(() => {
          navigate("/manage-cityprofile-data");
        });

        setFileSuccess("File uploaded successfully!");
        setFileError("");
      } else {
        setFileError(response.message);
      }
    } catch (error) {
      setFileError(error?.response?.data?.message);
      handleApiError(error);
    }
  };

  return (
    <Common headline="Insert City Profile Map's Data">
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 p-8 font-inter text-gray-800">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white gap-2 p-6 rounded-lg shadow-lg mb-8 flex flex-col md:flex-row justify-around items-center space-y-4 md:space-y-0 md:space-x-6 border border-blue-100">
            <div className="flex-1 w-full">
              <DropDown
                setter={(value) => {
                  setSelectedCity(value);
                }}
                label="Select City"
                placeholder="Select City"
                value={selectedCity}
                options={cityList?.map((city) => ({
                  ...city,
                  name: city?.name,
                  value: city?.id,
                }))}
                baggage={true}
              />
            </div>
            <div className="flex-1 w-full" style={{ marginTop: '0' }}>
              <DropDown
                setter={(value) => {
                  setSelectedYear(value);
                }}
                label="Select Year"
                placeholder="Select Year"
                value={selectedYear}
                options={Array.from({ length: 2035 - 2018 + 1 }, (_, i) => {
                  const year = 2018 + i;
                  return {
                    name: year.toString(),
                    value: year,
                  };
                })}
                baggage={true}
              />

            </div>
            <div className="flex-1 w-full" style={{ marginTop: '0' }}>
              <DropDown
                setter={(value) => {
                  setSelectedMapDataType(value);
                }}
                label="Select Map Data Type"
                placeholder="Select Map Data Type"
                value={selectedMapDataType}
                options={[
                  { value: '2', name: 'FSM & SWM Infrastructure Map Data' },
                  { value: '1', name: 'Ward Boundary Data', "option-disabled": true },
                ]}
                baggage={true}
              />
            </div>
          </div>

          {selectedCity && selectedYear && selectedMapDataType && (
            <div className="flex-1 w-full">
              <Card>
                <CardContent>
                  <div
                    {...getRootProps()}
                    style={{
                      border: "2px dashed #ccc",
                      borderRadius: "4px",
                      padding: "20px",
                      textAlign: "center",
                      cursor: "pointer",
                    }}
                  >
                    <input {...getInputProps()} />
                    <Typography variant="body1" className="p-2">
                      {isDragActive
                        ? "Drop the file here"
                        : `Drag 'n' drop ${selectedMapDataType?.value === 2 ? 'an Excel' : 'a Shapefile(.shp)'} file here, or click to select a file`}
                    </Typography>
                  </div>
                  {fileSuccess && (
                    <Typography color="success">{fileSuccess}</Typography>
                  )}
                  {fileError && <Typography color="error">{fileError}</Typography>}
                  <div style={{ marginTop: "20px", textAlign: "center" }} className={`${loading ? 'flex justify-center gap-3' : ''}`}>
                    {loading && <CircularProgress size={30} />}
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={!file || loading}
                      onClick={handleFileUpload}
                      className={`${loading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      Upload and Proceed
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

          )}
        </div>
      </div>
    </Common>
  );
}

export default InsertMapData;
